/**
 * Jersey Jackpot storefront behaviours.
 * Each feature is opt-in through data attributes, so sections work without JavaScript
 * and re-initialise cleanly when the theme editor reloads a section.
 */

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** @param {string} value */
const escapeHtml = (value) =>
  String(value).replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);

/* ------------------------------------------------------------------------------------------
 * Kit Bag: the cart drawer and every way of adding to it.
 * ---------------------------------------------------------------------------------------- */

const cartDrawer = () => /** @type {HTMLDialogElement | null} */ (document.querySelector('[data-jj-cart]'));

/** Variant ids currently in the cart, read from the rendered drawer. */
function cartVariantIds() {
  const panel = document.querySelector('[data-jj-cart-panel]');
  return (panel?.getAttribute('data-variants') || '').split(',').filter(Boolean);
}

/** Updates the count bubble on the header bag icon. */
function announceCartCount(count) {
  document.querySelectorAll('cart-icon').forEach((icon) => {
    const element = /** @type {any} */ (icon);
    if (typeof element.renderCartBubble === 'function') {
      element.renderCartBubble(count, false);
      return;
    }
    const bubble = icon.querySelector('.cart-bubble');
    const text = icon.querySelector('.cart-bubble__text-count');
    if (text) {
      text.textContent = String(count);
      text.classList.toggle('hidden', count === 0);
    }
    if (bubble) bubble.classList.toggle('visually-hidden', count === 0);
  });
}

/**
 * Keeps add buttons in step with the cart: a kit already in the bag reads "In bag" and opens
 * the drawer instead of adding again, since every kit is one of one.
 */
function syncAddButtons() {
  const inCart = cartVariantIds();

  document.querySelectorAll('[data-jj-add]').forEach((element) => {
    const button = /** @type {HTMLElement} */ (element);
    if (button.closest('[data-jj-cart]') || !button.dataset.jjAddedLabel) return;
    if (!button.dataset.jjLabel) button.dataset.jjLabel = (button.textContent || '').trim();
    const added = inCart.includes(button.dataset.jjAdd || '');
    button.toggleAttribute('data-jj-in-cart', added);
    button.textContent = added ? button.dataset.jjAddedLabel : button.dataset.jjLabel;
  });

  // Product page button: "Add to bag · $289.99 CAD", then "In your bag · View bag".
  const product = document.querySelector('[data-jj-product]');
  if (!product) return;
  let data = null;
  try {
    data = JSON.parse(product.textContent || 'null');
  } catch {
    data = null;
  }
  if (!data) return;
  document.querySelectorAll('.product-details button[name="add"], .sticky-add-to-cart button[name="add"]').forEach((element) => {
    const button = /** @type {HTMLButtonElement} */ (element);
    const added = inCart.includes(String(data.variantId));
    let label = button.querySelector('[data-jj-add-label]');
    if (!label) {
      const text = [...button.childNodes].find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
      if (!text) return;
      label = document.createElement('span');
      label.setAttribute('data-jj-add-label', '');
      label.dataset.original = text.textContent.trim();
      text.replaceWith(label);
    }
    const original = /** @type {HTMLElement} */ (label).dataset.original || '';
    button.toggleAttribute('data-jj-in-cart', added);
    if (added) label.textContent = data.inBagLabel;
    else if (data.available) label.textContent = `${data.addLabel} · ${data.price}`;
    else label.textContent = original;
  });
}

const PRIZE_KEY = 'jj_prize';

/** @returns {{ variantId: string, code: string } | null} The slot machine prize this shopper claimed. */
function readPrize() {
  try {
    const prize = JSON.parse(localStorage.getItem(PRIZE_KEY) || 'null');
    return prize && prize.variantId && prize.code ? prize : null;
  } catch {
    return null;
  }
}

/**
 * Keeps the slot machine deal in step with the bag. The deal is for a single kit: it applies
 * while the won kit is the only kit in the bag. With two or more kits the prize code comes
 * off, and Shopify's automatic Kit Bag discount (10% for two, 15% for three or more) takes over.
 */
async function syncPrize() {
  // @ts-ignore
  const routes = (typeof Theme !== 'undefined' && Theme.routes) || {};
  const updateUrl = (routes.cart_update_url || '/cart/update') + '.js';
  const setCodes = (codes) =>
    fetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') }),
    });

  try {
    const response = await fetch((routes.cart_url || '/cart') + '.js', { cache: 'no-store' });
    if (!response.ok) return;
    const cart = await response.json();
    const applied = (cart.discount_codes || []).map((entry) => String(entry.code));
    const prizeCodes = applied.filter((code) => code.toUpperCase().startsWith('JJ-'));
    const others = applied.filter((code) => !code.toUpperCase().startsWith('JJ-'));
    const prize = readPrize();
    // Accessories such as the wash bag ride along: only kits count towards the one-kit rule.
    const kits = (cart.items || []).filter((item) => item.product_type !== 'Accessories');
    const kitCount = kits.reduce((total, item) => total + item.quantity, 0);

    if (kitCount >= 2) {
      if (prizeCodes.length) await setCodes(others);
      return;
    }
    if (kitCount === 1 && prize && String(kits[0].variant_id) === String(prize.variantId)) {
      if (!prizeCodes.includes(prize.code)) await setCodes([...others, prize.code]);
      return;
    }
    // One item that is not the won kit, or an empty bag: a prize code has nothing to apply to.
    if (prizeCodes.length) await setCodes(others);
  } catch (error) {
    console.warn('[jj] prize sync:', error);
  }
}

/** Re-renders the drawer from the server and updates everything that depends on the cart. */
async function refreshCart() {
  const drawer = cartDrawer();
  const panel = drawer?.querySelector('[data-jj-cart-panel]');
  if (!drawer || !panel) return;
  panel.setAttribute('aria-busy', 'true');
  try {
    await syncPrize();
    const root = (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.root) || '/';
    const response = await fetch(`${root}?section_id=jj-cart-drawer`, { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`Cart refresh failed: ${response.status}`);
    const next = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-jj-cart-panel]');
    if (next) {
      const scroller = panel.querySelector('.jj-cart__items');
      const scrollTop = scroller ? scroller.scrollTop : 0;
      panel.innerHTML = next.innerHTML;
      panel.setAttribute('data-count', next.getAttribute('data-count') || '0');
      panel.setAttribute('data-variants', next.getAttribute('data-variants') || '');
      const nextScroller = panel.querySelector('.jj-cart__items');
      if (nextScroller) nextScroller.scrollTop = scrollTop;
    }
    announceCartCount(Number(panel.getAttribute('data-count') || 0));
    syncAddButtons();
  } catch (error) {
    console.warn('[jj] cart refresh:', error);
  } finally {
    panel.removeAttribute('aria-busy');
  }
}

/** Opens the drawer. Falls back to the cart page if the drawer is missing. */
function openCart() {
  const drawer = cartDrawer();
  if (!drawer) {
    window.location.href = (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_url) || '/cart';
    return;
  }
  document.querySelector('[data-jj-search]')?.close?.();
  if (!drawer.open) drawer.showModal();
}

/**
 * Adds a variant to the cart, optionally applies a discount code, then refreshes and opens
 * the drawer. Falls back to the cart page on any failure.
 * @param {string} variantId
 * @param {string} [discountCode]
 */
async function addToBag(variantId, discountCode) {
  // Horizon declares `Theme` with `const` in a classic script, so it is a global binding, not a window property.
  // @ts-ignore
  const routes = (typeof Theme !== 'undefined' && Theme.routes) || {};
  const cartUrl = routes.cart_url || '/cart';

  try {
    if (!cartVariantIds().includes(String(variantId))) {
      const response = await fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      // 422 means the kit is already in the bag or just sold out. Show the bag either way.
      if (!response.ok && response.status !== 422) throw new Error(`Add to cart failed: ${response.status}`);
    }
    if (discountCode) {
      // Apply the prize code to the cart itself, so the bag shows the saving straight away.
      const applied = await fetch((routes.cart_update_url || '/cart/update') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: discountCode }),
      });
      // Older cart endpoints ignore the field. The discount link still carries the code to checkout.
      if (!applied.ok) await fetch(`/discount/${encodeURIComponent(discountCode)}`, { credentials: 'same-origin' });
    }
    if (!cartDrawer()) {
      window.location.href = cartUrl;
      return;
    }
    await refreshCart();
    openCart();
  } catch (error) {
    console.warn('[jj] add to bag fell back to the cart page:', error);
    window.location.href = cartUrl;
  }
}

/**
 * Reports a slot machine event to Shopify's customer events. The "Jersey Jackpot spins" custom
 * pixel (Settings > Customer events) forwards these to Google Analytics.
 * @param {string} name - Event name, such as "jj_spin_result".
 * @param {Record<string, unknown>} data - Event details.
 */
function track(name, data) {
  try {
    /** @type {any} */ (window).Shopify?.analytics?.publish?.(name, data);
  } catch {
    /* tracking must never get in the way of the slot machine */
  }
}

/**
 * Claims a slot machine prize: fetches the won kit's own prize code from the server, adds the
 * kit, and applies the code. The code is valid for that kit only, and it only stays on while
 * that kit is the only item in the bag (see syncPrize).
 * @param {string} variantId
 * @param {string} tier - "any", "two" or "three".
 * @param {string} productUrl - The won kit's product URL.
 */
async function claimPrize(variantId, tier, productUrl) {
  let code = '';
  try {
    const joiner = productUrl.includes('?') ? '&' : '?';
    const response = await fetch(`${productUrl}${joiner}section_id=jj-spin-code-${tier}`, { cache: 'no-store' });
    if (response.ok) {
      const node = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-jj-spin-code]');
      code = (node?.textContent || '').trim();
    }
  } catch (error) {
    console.warn('[jj] claim prize:', error);
  }
  // Remember the prize. The bag applies it whenever the won kit is the only item in it.
  try {
    if (code) localStorage.setItem(PRIZE_KEY, JSON.stringify({ variantId: String(variantId), code }));
  } catch {
    /* storage unavailable: the deal applies now but is not restored later */
  }
  track('jj_deal_claimed', { tier, variant_id: String(variantId), product_url: productUrl, code_applied: Boolean(code) });
  await addToBag(variantId, code);
}

/** Removes a line from the cart by its key. */
async function removeFromBag(key) {
  const panel = document.querySelector('[data-jj-cart-panel]');
  panel?.setAttribute('aria-busy', 'true');
  try {
    // @ts-ignore
    const routes = (typeof Theme !== 'undefined' && Theme.routes) || {};
    const url = (routes.cart_change_url || '/cart/change') + '.js';
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    });
  } catch (error) {
    console.warn('[jj] remove from bag:', error);
  }
  await refreshCart();
}

// One delegated listener covers the bag icon, the drawer's controls, and every add button,
// including ones the drawer re-renders.
document.addEventListener(
  'click',
  async (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (!target?.closest) return;

    if (target.closest('[data-jj-cart-open]')) {
      event.preventDefault();
      openCart();
      refreshCart();
      return;
    }

    const drawer = cartDrawer();
    if (drawer && (target === drawer || target.closest('[data-jj-cart-close]'))) {
      drawer.close();
      return;
    }

    const remove = /** @type {HTMLElement | null} */ (target.closest('[data-jj-cart-remove]'));
    if (remove) {
      event.preventDefault();
      await removeFromBag(remove.dataset.jjCartRemove || '');
      return;
    }

    // A product page button for a kit that is already in the bag opens the bag instead.
    const inCartSubmit = target.closest('button[name="add"][data-jj-in-cart]');
    if (inCartSubmit) {
      event.preventDefault();
      event.stopPropagation();
      openCart();
      return;
    }

    const button = /** @type {HTMLElement | null} */ (target.closest('[data-jj-add]'));
    if (!button) return;
    event.preventDefault();
    const variantId = button.dataset.jjAdd;
    if (!variantId || button.hasAttribute('disabled')) return;
    // A kit that is already in the bag just opens the bag. A prize claim always goes through,
    // so the new code is applied even when the kit was added earlier.
    if (!button.dataset.jjSpinTier && cartVariantIds().includes(variantId)) {
      openCart();
      return;
    }
    button.setAttribute('disabled', '');
    if (button.dataset.jjSpinTier) await claimPrize(variantId, button.dataset.jjSpinTier, button.dataset.jjSpinUrl || '');
    else await addToBag(variantId, button.dataset.jjDiscount);
    button.removeAttribute('disabled');
  },
  true
);

// Horizon's own product form announces a successful add with a cart event. Pick that up,
// refresh the bag and open it.
import('@shopify/events')
  .then(({ StandardEvents }) => {
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      if (/** @type {any} */ (event).jjOwn) return;
      const done = /** @type {any} */ (event).promise;
      Promise.resolve(done)
        .catch(() => null)
        .then(async () => {
          await refreshCart();
          if (/** @type {any} */ (event).detail?.action !== 'remove') openCart();
        });
    });
  })
  .catch((error) => console.warn('[jj] cart events unavailable:', error));

/** Hero "Shop the Look" slideshow: cross-fading images, thumbnail picker, per-slide panel. */
function initHero(root) {
  root.querySelectorAll('[data-jj-hero]').forEach((hero) => {
    if (hero.dataset.jjReady) return;
    hero.dataset.jjReady = 'true';

    const slides = [...hero.querySelectorAll('[data-jj-slide]')];
    const thumbs = [...hero.querySelectorAll('[data-jj-thumb]')];
    const panels = [...hero.querySelectorAll('[data-jj-panel]')];
    if (slides.length === 0) return;

    let index = 0;
    let hold = false;
    let lastChange = Date.now();
    const interval = Number(hero.dataset.jjInterval || 6500);

    const show = (next) => {
      index = (next + slides.length) % slides.length;
      lastChange = Date.now();
      [slides, thumbs, panels].forEach((group) =>
        group.forEach((element, i) => element.classList.toggle('is-active', i === index))
      );
    };

    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => show(i)));
    const stage = hero.querySelector('[data-jj-stage]');
    // Resting a mouse on the picture pauses the rotation. Touches never pause it: a phone fires
    // no "leave" after a tap, which used to freeze the slideshow for the rest of the visit.
    stage?.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') hold = true;
    });
    stage?.addEventListener('pointerleave', () => {
      hold = false;
      lastChange = Date.now();
    });
    // A pause never lasts for ever, in case the pointer is simply parked over the picture.
    const maxHold = interval * 3;

    show(0);
    if (slides.length > 1 && !reducedMotion()) {
      const timer = setInterval(() => {
        if (!hero.isConnected) return clearInterval(timer);
        const waited = Date.now() - lastChange;
        if ((!hold && waited > interval) || waited > maxHold) show(index + 1);
      }, 1000);
    }
  });
}

/** Rails advance one card at a time, and pause for a while after the shopper touches them. */
function initAutoScroll(root) {
  root.querySelectorAll('[data-jj-autoscroll]').forEach((rail) => {
    if (rail.dataset.jjReady) return;
    rail.dataset.jjReady = 'true';
    if (reducedMotion()) return;

    let heldAt = 0;
    ['pointerdown', 'touchstart', 'wheel', 'mouseover', 'focusin'].forEach((type) =>
      rail.addEventListener(type, () => (heldAt = Date.now()), { passive: true })
    );

    const timer = setInterval(() => {
      if (!rail.isConnected) return clearInterval(timer);
      if (rail.scrollWidth <= rail.clientWidth + 4 || Date.now() - heldAt < 8000) return;
      const first = rail.firstElementChild;
      if (!first) return;
      const step = first.getBoundingClientRect().width + (parseFloat(getComputedStyle(rail).columnGap) || 0);
      if (rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4) {
        rail.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        rail.scrollBy({ left: step, behavior: 'smooth' });
      }
    }, 3500);
  });
}

/** Counts down to local midnight in HH:MM:SS. */
function initCountdowns(root) {
  root.querySelectorAll('[data-jj-countdown]').forEach((element) => {
    if (element.dataset.jjReady) return;
    element.dataset.jjReady = 'true';
    const pad = (value) => String(value).padStart(2, '0');
    const tick = () => {
      const end = new Date();
      end.setHours(24, 0, 0, 0);
      const left = Math.max(0, Math.floor((end.getTime() - Date.now()) / 1000));
      element.textContent = `${pad(Math.floor(left / 3600))}:${pad(Math.floor((left % 3600) / 60))}:${pad(left % 60)}`;
    };
    tick();
    const timer = setInterval(() => (element.isConnected ? tick() : clearInterval(timer)), 1000);
  });
}

const REEL_LENGTH = 50;
const REEL_DURATIONS = [1400, 1900, 2400];
const KITS_PER_DAY = 5;

/** "Today's Jackpot" slot machine. */
function initJackpot(root) {
  root.querySelectorAll('[data-jj-jackpot]').forEach(async (machine) => {
    if (machine.dataset.jjReady) return;
    machine.dataset.jjReady = 'true';

    /** @type {{ variantId: string, title: string, url: string, image: string, price: number, available: boolean, size?: string }[]} */
    let pool = [];
    const poolNode = machine.querySelector('[data-jj-jackpot-data]');
    try {
      pool = JSON.parse(poolNode?.textContent || '[]');
    } catch {
      pool = [];
    }

    // The page carries the first 250 products. Larger catalogues are read page by page from
    // this same section, so every kit with prize codes can land on the reels.
    const pages = Math.min(Number(poolNode?.dataset.pages || 1), 8);
    const sectionId = poolNode?.dataset.sectionId;
    if (pages > 1 && sectionId) {
      const more = await Promise.all(
        Array.from({ length: pages - 1 }, async (_, index) => {
          try {
            const url = new URL(window.location.href);
            url.searchParams.set('section_id', sectionId);
            url.searchParams.set('page', String(index + 2));
            const response = await fetch(url.toString());
            if (!response.ok) return [];
            const node = new DOMParser()
              .parseFromString(await response.text(), 'text/html')
              .querySelector('[data-jj-jackpot-data]');
            return JSON.parse(node?.textContent || '[]');
          } catch (error) {
            console.warn('[jj] jackpot pool:', error);
            return [];
          }
        })
      );
      pool = pool.concat(...more);
    }

    // A fixed order before the daily shuffle, so every shopper gets the same five kits.
    const seen = new Set();
    pool = pool
      .filter((kit) => !seen.has(kit.variantId) && seen.add(kit.variantId))
      .sort((first, second) => Number(first.variantId) - Number(second.variantId));
    pool = pool.filter((kit) => kit.available && kit.image);
    if (pool.length < KITS_PER_DAY) {
      machine.classList.add('jj-jackpot--empty');
      return;
    }

    // The same kits for every shopper on a given day: a date-seeded shuffle. With a size picked,
    // only kits in that size are drawn, so there can be fewer than five.
    const today = new Date();
    const pickDaily = (size) => {
      let seed = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
      const random = () => {
        seed = (seed * 1664525 + 1013904223) % 4294967296;
        return seed / 4294967296;
      };
      const shuffled = pool.filter((kit) => !size || kit.size === size);
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled.slice(0, KITS_PER_DAY);
    };

    const picker = machine.querySelector('[data-jj-size-picker]');
    const sizeOptions = picker
      ? (picker.dataset.sizes || '')
          .split(',')
          .map((size) => size.trim())
          .filter(Boolean)
          .map((size) => ({ size, count: pool.filter((kit) => kit.size === size).length }))
          .filter((option) => option.count > 0)
      : [];
    const SIZE_KEY = 'jj_jp_size';
    let chosenSize = '';
    try {
      const saved = localStorage.getItem(SIZE_KEY) || '';
      if (sizeOptions.some((option) => option.size === saved)) chosenSize = saved;
    } catch {
      chosenSize = '';
    }
    // The size step only shows when kits carry sizes. Without it the machine works as before.
    const needsSize = () => sizeOptions.length > 0 && !chosenSize;
    let daily = pickDaily(chosenSize);

    const config = machine.dataset;
    const tiers = {
      1: { percent: Number(config.percentAny || 10), tier: 'any', label: config.labelAny || '' },
      2: { percent: Number(config.percentTwo || 15), tier: 'two', label: config.labelTwo || '' },
      3: { percent: Number(config.percentThree || 20), tier: 'three', label: config.labelThree || '' },
    };
    const spinLimit = Number(config.spinLimit || 0);
    const storageKey = `jj_spins_${today.getFullYear()}_${today.getMonth() + 1}_${today.getDate()}`;
    const readSpins = () => {
      try {
        return Number(localStorage.getItem(storageKey) || 0);
      } catch {
        return 0;
      }
    };
    const writeSpins = (value) => {
      try {
        localStorage.setItem(storageKey, String(value));
      } catch {
        /* storage unavailable: the limit simply does not persist */
      }
    };
    const formatMoney = (cents) =>
      (config.moneyFormat || '${{amount}}').replace(/\{\{\s*amount[^}]*\}\}/, (cents / 100).toFixed(2));

    const strips = [...machine.querySelectorAll('[data-jj-reel-strip]')];
    const frames = [...machine.querySelectorAll('[data-jj-reel]')];
    const spinButton = machine.querySelector('[data-jj-spin]');
    const lever = machine.querySelector('[data-jj-lever]');
    const idlePanel = machine.querySelector('[data-jj-jackpot-idle]');
    const resultPanel = machine.querySelector('[data-jj-jackpot-result]');
    const note = machine.querySelector('[data-jj-spins-note]');
    const list = machine.querySelector('[data-jj-jackpot-list]');

    const pitch = machine.querySelector('[data-jj-jackpot-pitch]');
    const listHeading = machine.querySelector('[data-jj-list-heading]');
    const sizeNote = machine.querySelector('[data-jj-size-note]');
    const sizeButtons = machine.querySelector('[data-jj-size-options]');

    /** Fills the reels, the "on today's reels" list and the size step for the current size. */
    const fill = () => {
      strips.forEach((strip) => {
        strip.innerHTML = Array.from({ length: REEL_LENGTH }, (_, i) => {
          const kit = daily[i % daily.length];
          return `<img src="${kit.image}" alt="" decoding="async" width="150" height="188">`;
        }).join('');
      });

      if (list) {
        list.innerHTML = daily
          .map(
            (kit) => `<a class="jj-mini" href="${kit.url}">
            <span class="jj-mini__img"><img src="${kit.image}" alt="" decoding="async" width="56" height="70"></span>
            <span class="jj-mini__text"><span class="jj-mini__title">${escapeHtml(kit.title)}</span><span class="jj-mini__price">${formatMoney(kit.price)}</span></span>
          </a>`
          )
          .join('');
      }

      machine.classList.toggle('needs-size', needsSize());
      if (!picker || !sizeOptions.length) return;
      picker.hidden = false;
      if (sizeButtons) {
        sizeButtons.innerHTML = sizeOptions
          .map(
            (option) => `<button type="button" class="jj-jackpot__size" data-jj-size="${escapeHtml(option.size)}" aria-pressed="${option.size === chosenSize}">
            <b>${escapeHtml(option.size)}</b><small>${option.count} ${option.count === 1 ? 'kit' : 'kits'}</small>
          </button>`
          )
          .join('');
      }
      if (sizeNote) {
        sizeNote.textContent = chosenSize
          ? (picker.dataset.noteChosen || '').replace('[size]', chosenSize)
          : picker.dataset.noteNone || '';
      }
      if (listHeading) {
        listHeading.textContent = `${listHeading.dataset.heading || ''} · ${chosenSize ? `size ${chosenSize}` : 'all sizes'}`;
      }
      if (pitch) {
        const kits = daily.length === 1 ? `size ${chosenSize} kit` : `${daily.length} size ${chosenSize} kits`;
        pitch.textContent = chosenSize
          ? `Spin the reels to unlock a deal on one of today's ${kits}.`
          : picker.dataset.pitchNone || pitch.dataset.pitch || '';
      }
    };

    let positions = [0, 1, 2];
    let spinning = false;
    const place = (animate) =>
      strips.forEach((strip, i) => {
        strip.style.transition = animate ? `transform ${REEL_DURATIONS[i]}ms cubic-bezier(.17,.84,.3,1.02)` : 'none';
        strip.style.transform = `translateY(calc(var(--jj-reel-h) * ${-positions[i]}))`;
      });
    fill();
    place(false);

    const updateNote = () => {
      const used = readSpins();
      const left = spinLimit > 0 ? Math.max(0, spinLimit - used) : Infinity;
      if (note) {
        const count = left === Infinity ? config.noteUnlimited || '' : `${left} ${left === 1 ? 'spin' : 'spins'} left today.`;
        note.textContent = `${count} ${config.noteSuffix || ''}`.trim();
      }
      if (spinButton && !spinning) {
        spinButton.toggleAttribute('disabled', left <= 0);
        let label = config.labelSpin || 'Spin';
        if (needsSize()) label = picker?.dataset.labelPick || 'Pick a size first';
        else if (left <= 0) label = config.labelDone || 'Back at midnight';
        else if (resultPanel?.hidden === false) label = config.labelAgain || 'Spin again';
        spinButton.textContent = label;
      }
      return left;
    };

    const showResult = (targets) => {
      const counts = {};
      targets.forEach((value) => (counts[value] = (counts[value] || 0) + 1));
      const [topValue, tier] = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
      const prizeIndex = tier > 1 ? Number(topValue) : targets[1];
      const kit = daily[prizeIndex];
      const offer = tiers[tier];

      frames.forEach((frame, i) => frame.classList.toggle('is-hit', targets[i] === prizeIndex));
      if (!resultPanel) return { kit, offer };

      const set = (selector, value) => {
        const element = resultPanel.querySelector(selector);
        if (element) element.textContent = value;
      };
      set('[data-jj-win-tier]', offer.label);
      set('[data-jj-win-title]', kit.title);
      set('[data-jj-win-deal]', formatMoney(kit.price - Math.floor((kit.price * offer.percent) / 100)));
      set('[data-jj-win-was]', formatMoney(kit.price));
      resultPanel.querySelectorAll('[data-jj-win-link]').forEach((link) => link.setAttribute('href', kit.url));
      const image = resultPanel.querySelector('[data-jj-win-image]');
      if (image) {
        image.setAttribute('src', kit.image);
        image.setAttribute('alt', kit.title);
      }
      const claim = resultPanel.querySelector('[data-jj-add]');
      if (claim) {
        claim.dataset.jjAdd = kit.variantId;
        claim.dataset.jjSpinTier = offer.tier;
        claim.dataset.jjSpinUrl = kit.url;
        claim.textContent = config.labelClaim || 'Claim deal';
      }
      if (idlePanel) idlePanel.hidden = true;
      resultPanel.hidden = false;
      return { kit, offer };
    };

    const spin = () => {
      if (spinning) return;
      if (needsSize()) {
        // No size yet: point at the size step instead of spinning.
        picker?.classList.add('is-nudged');
        picker?.scrollIntoView({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
        setTimeout(() => picker?.classList.remove('is-nudged'), 1600);
        return;
      }
      if (updateNote() <= 0 || !daily.length) return;
      const count = daily.length;
      spinning = true;
      machine.classList.add('is-spinning');
      frames.forEach((frame) => frame.classList.remove('is-hit'));
      if (spinButton) spinButton.textContent = config.labelSpinning || 'Spinning…';

      const targets =
        Math.random() < 0.12
          ? Array(3).fill(Math.floor(Math.random() * count))
          : [0, 1, 2].map(() => Math.floor(Math.random() * count));

      positions = positions.map((position) => position % count);
      place(false);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          positions = targets.map(
            (target, i) => positions[i] + count * (6 + i) + ((target - positions[i] + count) % count)
          );
          place(!reducedMotion());
        })
      );

      setTimeout(
        () => {
          spinning = false;
          machine.classList.remove('is-spinning');
          positions = targets;
          place(false);
          writeSpins(readSpins() + 1);
          const { kit, offer } = showResult(targets);
          const left = updateNote();
          track('jj_spin_result', {
            tier: offer.tier,
            percent_off: offer.percent,
            product_title: kit.title,
            product_url: kit.url,
            variant_id: String(kit.variantId),
            price: kit.price / 100,
            size: chosenSize || 'any',
            spins_used: readSpins(),
            spins_left: left === Infinity ? -1 : left,
          });
          if (left === 0) track('jj_spin_limit_reached', { spins_used: readSpins(), size: chosenSize || 'any' });
        },
        reducedMotion() ? 50 : REEL_DURATIONS[2] + 250
      );
    };

    spinButton?.addEventListener('click', spin);
    lever?.addEventListener('click', spin);

    // Picking a size redraws today's kits for that size and clears any result on show.
    sizeButtons?.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-jj-size]') : null;
      if (!(button instanceof HTMLElement) || spinning) return;
      chosenSize = button.dataset.jjSize || '';
      track('jj_size_selected', { size: chosenSize });
      try {
        localStorage.setItem(SIZE_KEY, chosenSize);
      } catch {
        /* storage unavailable: the size is asked for again next visit */
      }
      daily = pickDaily(chosenSize);
      positions = [0, 1, 2];
      frames.forEach((frame) => frame.classList.remove('is-hit'));
      if (resultPanel) resultPanel.hidden = true;
      if (idlePanel) idlePanel.hidden = false;
      fill();
      place(false);
      updateNote();
    });

    updateNote();
  });
}

/**
 * Turns flat-lay product photos shot on white into clean cut-outs for the shirt collages.
 *
 * Steps:
 * 1. Flood-fill pure white inward from the image edges to find the backdrop.
 * 2. Trim the soft, near-white fringe that JPEG compression and studio shadows leave around the
 *    shirt, a few pixels at most so a white shirt is never eaten.
 * 3. Drop small stray fragments, such as a hanger tip or a patch of shadow.
 * 4. Remove the white that is blended into the remaining edge pixels, so no light outline shows
 *    on dark tiles.
 * If the result looks wrong, the original photo is kept.
 * Finished cut-outs are kept in the browser's cache storage, so later visits show them at once.
 * @param {HTMLImageElement} image
 * @returns {Promise<void>} Settles when the image has been handled.
 */
async function cutOut(image) {
  const original = image.getAttribute('src') || image.currentSrc;
  if (!original || image.dataset.jjCut) return;
  image.dataset.jjCut = 'pending';
  // The tiles show these shirts small, so a medium rendition is enough and is quick to process.
  const source = new URL(original.replace(/([?&])width=\d+/, `$1width=${CUTOUT_SIZE}`), location.href).href;

  /** @param {Blob} blob */
  const show = (blob) => {
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.loading = 'eager';
    image.src = URL.createObjectURL(blob);
    image.dataset.jjCut = 'done';
  };
  const skip = () => {
    image.loading = 'eager';
    image.dataset.jjCut = 'skipped';
  };

  /** @type {Cache | null} */
  let store = null;
  try {
    store = await caches.open(CUTOUT_CACHE);
    const saved = await store.match(source);
    if (saved) {
      show(await saved.blob());
      return;
    }
  } catch {
    /* cache storage unavailable: the cut-out is made again on each visit */
  }

  await new Promise((resolve) => {
  const done = () => resolve(undefined);
  const loader = new Image();
  loader.crossOrigin = 'anonymous';
  loader.onload = () => {
    try {
      const scale = Math.min(1, CUTOUT_SIZE / Math.max(loader.naturalWidth, loader.naturalHeight));
      const width = Math.max(1, Math.round(loader.naturalWidth * scale));
      const height = Math.max(1, Math.round(loader.naturalHeight * scale));
      const total = width * height;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) {
        skip();
        done();
        return;
      }
      context.drawImage(loader, 0, 0, width, height);
      const frame = context.getImageData(0, 0, width, height);
      const pixels = frame.data;

      const lightest = (position) =>
        Math.min(pixels[position * 4], pixels[position * 4 + 1], pixels[position * 4 + 2]);
      const spread = (position) =>
        Math.max(pixels[position * 4], pixels[position * 4 + 1], pixels[position * 4 + 2]) - lightest(position);
      /** @param {(x: number, y: number, position: number) => void} visit */
      const eachNeighbour = (position, visit) => {
        const x = position % width;
        const y = (position - x) / width;
        if (x > 0) visit(x - 1, y, position - 1);
        if (x < width - 1) visit(x + 1, y, position + 1);
        if (y > 0) visit(x, y - 1, position - width);
        if (y < height - 1) visit(x, y + 1, position + width);
      };

      // 1. Backdrop: pure white connected to the image edge.
      const removed = new Uint8Array(total);
      const queue = new Int32Array(total);
      let head = 0;
      let tail = 0;
      const seed = (position) => {
        if (removed[position] || lightest(position) < 243 || spread(position) > 8) return;
        removed[position] = 1;
        queue[tail++] = position;
      };
      for (let x = 0; x < width; x++) {
        seed(x);
        seed((height - 1) * width + x);
      }
      for (let y = 0; y < height; y++) {
        seed(y * width);
        seed(y * width + width - 1);
      }
      while (head < tail) eachNeighbour(queue[head++], (x, y, next) => seed(next));

      const share = tail / total;
      // Too little removed means the photo is not on white. Too much means the fill leaked into the shirt.
      if (share < 0.12 || share > 0.85) {
        image.dataset.jjCut = 'skipped';
        return;
      }

      // 2. Trim the near-white fringe, one ring at a time.
      const rings = Math.max(2, Math.round(Math.max(width, height) / 220));
      for (let ring = 0; ring < rings; ring++) {
        const fringe = [];
        for (let position = 0; position < total; position++) {
          if (removed[position] || lightest(position) < 214 || spread(position) > 22) continue;
          let touches = false;
          eachNeighbour(position, (x, y, next) => {
            if (removed[next]) touches = true;
          });
          if (touches) fringe.push(position);
        }
        if (fringe.length === 0) break;
        for (const position of fringe) removed[position] = 1;
      }

      // 3. Keep the shirt and anything of similar size. Drop small stray fragments.
      const label = new Int32Array(total);
      const sizes = [0];
      for (let start = 0; start < total; start++) {
        if (removed[start] || label[start]) continue;
        const id = sizes.length;
        let count = 0;
        head = 0;
        tail = 0;
        label[start] = id;
        queue[tail++] = start;
        while (head < tail) {
          const position = queue[head++];
          count++;
          eachNeighbour(position, (x, y, next) => {
            if (removed[next] || label[next]) return;
            label[next] = id;
            queue[tail++] = next;
          });
        }
        sizes.push(count);
      }
      const largest = Math.max(...sizes);
      for (let position = 0; position < total; position++) {
        if (!removed[position] && sizes[label[position]] < largest * 0.04) removed[position] = 1;
      }

      // Smooth the outline: a pixel follows the majority of its eight neighbours, which removes
      // the ragged steps left where a white shirt meets the white backdrop.
      for (let pass = 0; pass < 3; pass++) {
        const next = removed.slice();
        for (let y = 1; y < height - 1; y++) {
          for (let x = 1; x < width - 1; x++) {
            const position = y * width + x;
            const gone =
              removed[position - width - 1] + removed[position - width] + removed[position - width + 1] +
              removed[position - 1] + removed[position + 1] +
              removed[position + width - 1] + removed[position + width] + removed[position + width + 1];
            if (gone >= 5) next[position] = 1;
            else if (gone <= 3) next[position] = 0;
          }
        }
        removed.set(next);
      }

      // 4. Edge pixels still carry some backdrop white. Estimate how much, take it out of the
      //    colour, and fade the pixel by the same amount.
      const depth = new Uint8Array(total);
      let band = [];
      for (let position = 0; position < total; position++) {
        if (removed[position]) continue;
        let touches = false;
        eachNeighbour(position, (x, y, next) => {
          if (removed[next]) touches = true;
        });
        if (touches) {
          depth[position] = 1;
          band.push(position);
        }
      }
      const second = [];
      for (const position of band) {
        eachNeighbour(position, (x, y, next) => {
          if (removed[next] || depth[next]) return;
          depth[next] = 2;
          second.push(next);
        });
      }
      band = band.concat(second);
      for (const position of band) {
        const index = position * 4;
        const white = lightest(position);
        // How much of this pixel is shirt rather than backdrop. Inner-ring pixels are mostly shirt.
        const floor = depth[position] === 1 ? 0.15 : 0.7;
        const cover = Math.min(1, Math.max(floor, (255 - white) / 90));
        if (cover >= 1) continue;
        for (let channel = 0; channel < 3; channel++) {
          const value = (pixels[index + channel] - 255 * (1 - cover)) / cover;
          pixels[index + channel] = Math.max(0, Math.min(255, Math.round(value)));
        }
        pixels[index + 3] = Math.round(255 * cover);
      }
      for (let position = 0; position < total; position++) {
        if (removed[position]) pixels[position * 4 + 3] = 0;
      }

      context.putImageData(frame, 0, 0);
      canvas.toBlob((blob) => {
        if (!blob) {
          skip();
          done();
          return;
        }
        show(blob);
        store?.put(source, new Response(blob, { headers: { 'Content-Type': 'image/png' } })).catch(() => {});
        done();
      }, 'image/png');
    } catch (error) {
      skip();
      done();
    }
  };
  loader.onerror = () => {
    skip();
    done();
  };
  loader.src = source;
  });
}

const CUTOUT_SIZE = 600;
const CUTOUT_CACHE = 'jj-cutouts-v1';

/** Runs a task when the browser is idle, or shortly after where that is not supported. */
const whenIdle = (task) => {
  if ('requestIdleCallback' in window) window.requestIdleCallback(task, { timeout: 600 });
  else window.setTimeout(task, 60);
};

/**
 * Cuts out the collage images one after another, starting as soon as the page is ready, so
 * they are already in place when the shopper scrolls to them.
 */
function initCutOuts(root) {
  const images = /** @type {HTMLImageElement[]} */ ([...root.querySelectorAll('[data-jj-cutout] img')]);
  if (images.length === 0) return;
  let next = 0;
  const step = () => {
    const image = images[next++];
    if (!image) return;
    cutOut(image).then(() => whenIdle(step), () => whenIdle(step));
  };
  // Two at a time keeps the page responsive while the work is done.
  whenIdle(step);
  whenIdle(step);
}

/**
 * Fetches the pictures further down the page in the background once the page has opened, so
 * they are ready before the shopper scrolls to them. Second (hover) pictures go last. Shoppers
 * who asked their browser to save data keep on-demand loading.
 */
function initImageWarmup() {
  const connection = /** @type {any} */ (navigator).connection;
  if (connection && (connection.saveData || /[23]g/.test(connection.effectiveType || ''))) return;
  const start = () => {
    const pending = /** @type {HTMLImageElement[]} */ ([...document.querySelectorAll('img[loading="lazy"]')]).filter(
      (image) => !image.closest('[data-jj-cutout]') && !image.complete
    );
    const isHover = (image) => Boolean(image.closest('.jj-kit-card__img2, .jj-card-hover'));
    const queue = [...pending.filter((image) => !isHover(image)), ...pending.filter(isHover)].slice(0, 240);
    let active = 0;
    const pump = () => {
      while (active < 6 && queue.length > 0) {
        const image = /** @type {HTMLImageElement} */ (queue.shift());
        if (image.complete || !image.isConnected) continue;
        active++;
        const settle = () => {
          active--;
          pump();
        };
        image.addEventListener('load', settle, { once: true });
        image.addEventListener('error', settle, { once: true });
        image.decoding = 'async';
        image.loading = 'eager';
      }
    };
    pump();
  };
  if (document.readyState === 'complete') whenIdle(start);
  else window.addEventListener('load', () => whenIdle(start), { once: true });
}

/**
 * Search panel. Opens from any [data-jj-search-open] control, shows popular searches while the
 * input is empty, and loads live results from the jj-search-results section as the shopper types.
 */
function initSearch() {
  const panel = /** @type {HTMLDialogElement | null} */ (document.querySelector('[data-jj-search]'));
  if (!panel || panel.dataset.jjReady) return;
  panel.dataset.jjReady = 'true';

  const input = /** @type {HTMLInputElement | null} */ (panel.querySelector('[data-jj-search-input]'));
  const results = panel.querySelector('[data-jj-search-results]');
  const popular = panel.querySelector('[data-jj-search-popular]');
  if (!input || !results) return;

  const searchUrl = panel.dataset.searchUrl || '/search';
  /** @type {AbortController | null} */
  let request = null;
  let timer = 0;

  const render = async (term) => {
    request?.abort();
    if (!term) {
      results.innerHTML = '';
      results.removeAttribute('aria-busy');
      if (popular) popular.hidden = false;
      return;
    }
    request = new AbortController();
    results.setAttribute('aria-busy', 'true');
    try {
      const query = new URLSearchParams({ q: toQuery(term), type: 'product', 'options[prefix]': 'last', section_id: 'jj-search-results' });
      const response = await fetch(`${searchUrl}?${query}`, { signal: request.signal });
      if (!response.ok) throw new Error(`Search failed: ${response.status}`);
      const payload = new DOMParser()
        .parseFromString(await response.text(), 'text/html')
        .querySelector('[data-jj-search-payload]');
      if (input.value.trim() !== term) return;
      results.innerHTML = payload ? payload.innerHTML : '';
      if (popular) popular.hidden = true;
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.warn('[jj] search:', error);
    } finally {
      results.removeAttribute('aria-busy');
    }
  };

  const schedule = () => {
    clearTimeout(timer);
    const term = input.value.trim();
    timer = window.setTimeout(() => render(term), term ? 220 : 0);
  };

  // A decade such as "00s" or "90s" is searched as the start of its years ("200", "199"),
  // because seasons appear in product titles as years.
  const toQuery = (term) => {
    const decade = term.match(/^(d)0s$/i);
    if (!decade) return term;
    return (Number(decade[1]) >= 3 ? "19" : "20") + decade[1];
  };

  input.addEventListener('input', schedule);

  panel.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target === panel || target.closest('[data-jj-search-close]')) {
      panel.close();
      return;
    }
    const pill = /** @type {HTMLElement | null} */ (target.closest('[data-jj-search-term]'));
    if (pill) {
      input.value = pill.dataset.jjSearchTerm || '';
      input.focus();
      schedule();
    }
  });

  document.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (!target?.closest?.('[data-jj-search-open]')) return;
    event.preventDefault();
    if (!panel.open) panel.showModal();
    setTimeout(() => input.focus(), 60);
  });
}

/** Filters a list of items by a text input, for the players index. */
function initFilters(root) {
  root.querySelectorAll('[data-jj-filter-input]').forEach((input) => {
    if (input.dataset.jjReady) return;
    input.dataset.jjReady = 'true';
    const scope = input.closest('[data-jj-filter]') || root;
    const items = [...scope.querySelectorAll('[data-jj-filter-item]')];
    const empty = scope.querySelector('[data-jj-filter-empty]');
    input.addEventListener('input', () => {
      const query = input.value.trim().toLowerCase();
      let shown = 0;
      items.forEach((item) => {
        const match = (item.dataset.jjFilterItem || '').toLowerCase().includes(query);
        item.hidden = !match;
        if (match) shown++;
      });
      if (empty) empty.hidden = shown > 0;
    });
  });
}

/** Opens and closes native dialogs, including a click on the backdrop. */
/**
 * Reads one page of "league|era" entries for a collection.
 * @param {string} url - The collection URL without tags.
 * @param {number} pageNumber
 * @returns {Promise<{ entries: string[], pages: number }>}
 */
async function fetchFacetPage(url, pageNumber) {
  const response = await fetch(`${url}?section_id=jj-facets&page=${pageNumber}`);
  if (!response.ok) return { entries: [], pages: 1 };
  const node = new DOMParser()
    .parseFromString(await response.text(), 'text/html')
    .querySelector('[data-jj-facet-data]');
  return {
    entries: JSON.parse(node?.textContent || '[]'),
    pages: Math.min(Number(node?.getAttribute('data-pages') || 1), 12),
  };
}

/**
 * Puts kit counts on the league and era pills of a collection page, and hides pills that
 * would lead to an empty page. A league count respects the chosen era, and the other way round.
 */
function initFacets(root) {
  root.querySelectorAll('[data-jj-facets]').forEach(async (facets) => {
    if (!(facets instanceof HTMLElement) || facets.dataset.jjReady) return;
    facets.dataset.jjReady = 'true';
    const { url = '', league: chosenLeague = '', era: chosenEra = '' } = facets.dataset;
    if (!url) return;

    /** @type {string[]} */
    let entries = [];
    const cacheKey = `jj_facets_${url}`;
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      if (cached && Date.now() - cached.time < 5 * 60 * 1000) entries = cached.entries;
    } catch {
      /* no cache */
    }

    if (!entries.length) {
      try {
        const first = await fetchFacetPage(url, 1);
        const rest = await Promise.all(
          Array.from({ length: first.pages - 1 }, (_, index) => fetchFacetPage(url, index + 2))
        );
        entries = first.entries.concat(...rest.map((page) => page.entries));
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ time: Date.now(), entries }));
        } catch {
          /* storage unavailable */
        }
      } catch (error) {
        console.warn('[jj] facets:', error);
        return;
      }
    }

    const kits = entries.map((entry) => {
      const [league = '', era = ''] = String(entry).split('|');
      return { league, era };
    });

    facets.querySelectorAll('[data-jj-facet]').forEach((pill) => {
      if (!(pill instanceof HTMLElement)) return;
      const value = pill.dataset.value || '';
      const count =
        pill.dataset.jjFacet === 'league'
          ? kits.filter((kit) => kit.league === value && (!chosenEra || kit.era === chosenEra)).length
          : kits.filter((kit) => kit.era === value && (!chosenLeague || kit.league === chosenLeague)).length;
      const label = pill.querySelector('[data-jj-facet-count]');
      if (label) label.textContent = ` (${count})`;
      pill.hidden = count === 0 && !pill.classList.contains('is-active');
    });

    facets.querySelectorAll('[data-jj-facet-row]').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      const pills = /** @type {HTMLElement[]} */ ([...row.querySelectorAll('[data-jj-facet]')]);
      row.hidden = !pills.some((pill) => !pill.hidden);
    });
  });
}

/**
 * Recently Viewed: remembers the kits a shopper opens and shows the others on each product page.
 * Sold kits are skipped, because their card comes back empty.
 */
function initRecentlyViewed(root) {
  root.querySelectorAll('[data-jj-recent]').forEach(async (section) => {
    if (!(section instanceof HTMLElement) || section.dataset.jjReady) return;
    section.dataset.jjReady = 'true';
    const { current = '', root: rootUrl = '/' } = section.dataset;
    const limit = Number(section.dataset.limit || 5);
    const key = 'jj_recent';
    /** @type {string[]} */
    let seen = [];
    try {
      const saved = JSON.parse(localStorage.getItem(key) || '[]');
      if (Array.isArray(saved)) seen = saved.filter((handle) => typeof handle === 'string');
    } catch {
      seen = [];
    }

    if (current) {
      try {
        localStorage.setItem(key, JSON.stringify([current, ...seen.filter((handle) => handle !== current)].slice(0, 12)));
      } catch {
        /* storage unavailable: nothing is remembered */
      }
    }

    const list = section.querySelector('[data-jj-recent-list]');
    const others = seen.filter((handle) => handle !== current).slice(0, limit + 3);
    if (!list || !others.length) return;

    const cards = await Promise.all(
      others.map(async (handle) => {
        try {
          const response = await fetch(`${rootUrl}products/${encodeURIComponent(handle)}?section_id=jj-kit-card`);
          if (!response.ok) return null;
          return new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('.jj-kit-card');
        } catch {
          return null;
        }
      })
    );
    const ready = cards.filter((card) => card instanceof HTMLElement).slice(0, limit);
    if (!ready.length) return;
    list.replaceChildren(...ready);
    section.hidden = false;
  });
}

/** Orders the club tiles by kits in stock, most first, then by name. */
function initClubIndex(root) {
  root.querySelectorAll('[data-jj-club-index]').forEach((grid) => {
    if (!(grid instanceof HTMLElement) || grid.dataset.jjReady) return;
    grid.dataset.jjReady = 'true';
    const tiles = /** @type {HTMLElement[]} */ ([...grid.querySelectorAll('.jj-clubs__tile')]);
    tiles
      .sort(
        (first, second) =>
          Number(second.dataset.count) - Number(first.dataset.count) ||
          (first.dataset.name || '').localeCompare(second.dataset.name || '')
      )
      .forEach((tile) => grid.insertBefore(tile, grid.querySelector('.jj-clubs__empty')));
  });
}

/** Links to the size guide page open the site-wide size guide dialog instead of leaving the page. */
let sizeGuideLinksReady = false;
function initSizeGuideLinks() {
  if (sizeGuideLinksReady) return;
  sizeGuideLinksReady = true;
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!(target instanceof HTMLAnchorElement)) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || target.target === '_blank') return;
    const path = target.pathname.replace(/\/+$/, '');
    if (!path.endsWith('/pages/size-guide') || window.location.pathname.replace(/\/+$/, '') === path) return;
    const dialog = document.getElementById('jj-size-guide');
    if (!(dialog instanceof HTMLDialogElement)) return;
    event.preventDefault();
    if (!dialog.open) dialog.showModal();
  });
}

function initDialogs(root) {
  initSizeGuideLinks();
  root.querySelectorAll('[data-jj-dialog-open]').forEach((trigger) => {
    if (trigger.dataset.jjReady) return;
    trigger.dataset.jjReady = 'true';
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      const dialog = document.getElementById(trigger.dataset.jjDialogOpen || '');
      if (dialog instanceof HTMLDialogElement && !dialog.open) dialog.showModal();
    });
  });
  root.querySelectorAll('dialog.jj-dialog').forEach((dialog) => {
    if (dialog.dataset.jjReady) return;
    dialog.dataset.jjReady = 'true';
    dialog.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      if (target === dialog || target.closest('[data-jj-dialog-close]')) dialog.close();
    });
  });
}

/**
 * Keeps sold-out products at the end of collection and search grids. Each page from the server
 * already lists in-stock products first; for a list longer than one page, the remaining pages
 * are fetched straight away and merged in, in-stock products ahead of every sold-out one.
 */
function initStockOrder() {
  const list = document.querySelector('results-list');
  if (!(list instanceof HTMLElement) || list.dataset.jjStockOrder) return;
  list.dataset.jjStockOrder = 'true';
  const sectionId = list.getAttribute('section-id');
  if (!sectionId) return;

  const merge = async () => {
    const grid = /** @type {HTMLElement | null} */ (list.querySelector('.product-grid[data-last-page]'));
    if (!grid) return;
    const pages = Number(grid.dataset.lastPage);
    // A shopper who opened a later page directly keeps the theme's own paging.
    if (!(pages > 1) || new URLSearchParams(location.search).has('page')) return;
    // One page as far as the theme's own loader is concerned: the rest is added here.
    grid.dataset.lastPage = '1';
    const address = location.href;
    try {
      const documents = await Promise.all(
        Array.from({ length: pages - 1 }, async (_, index) => {
          const url = new URL(address);
          url.searchParams.set('page', String(index + 2));
          url.searchParams.set('section_id', sectionId);
          const response = await fetch(url.toString());
          if (!response.ok) throw new Error(`page ${index + 2}: ${response.status}`);
          return new DOMParser().parseFromString(await response.text(), 'text/html');
        })
      );
      // The filters or sort changed while the pages were loading: that view merges itself.
      if (!grid.isConnected || location.href !== address) return;
      documents.forEach((page) => {
        page.querySelectorAll('.product-grid > .product-grid__item').forEach((item) => {
          const firstSoldOut = grid.querySelector(':scope > .product-grid__item[data-jj-sold]');
          if (item.hasAttribute('data-jj-sold') || !firstSoldOut) grid.append(item);
          else firstSoldOut.before(item);
        });
      });
    } catch (error) {
      console.warn('[jj] stock order:', error);
      grid.dataset.lastPage = String(pages);
    }
  };

  merge();
  // Filtering or sorting redraws the grid with a fresh page count; merge that view too.
  new MutationObserver(() => {
    const grid = /** @type {HTMLElement | null} */ (list.querySelector('.product-grid[data-last-page]'));
    if (grid && Number(grid.dataset.lastPage) > 1) merge();
  }).observe(list, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-last-page'] });
}

/**
 * Forces a fresh layout of the custom sections after a real rotation or window width change.
 * Width changes while the page is still opening are ignored: Instagram's and Facebook's in-app
 * browsers report them as their panel slides in, and re-laying out then could leave the
 * shopper partway down the page. The shopper's scroll position is kept either way.
 */
let lastLayoutWidth = window.innerWidth;
let layoutSettled = false;
const settleLayout = () =>
  window.setTimeout(() => {
    layoutSettled = true;
    lastLayoutWidth = window.innerWidth;
  }, 1500);
if (document.readyState === 'complete') settleLayout();
else window.addEventListener('load', settleLayout, { once: true });
function relayoutAfterRotation() {
  if (!layoutSettled) {
    lastLayoutWidth = window.innerWidth;
    return;
  }
  if (Math.abs(window.innerWidth - lastLayoutWidth) < 40) return;
  lastLayoutWidth = window.innerWidth;
  const scrollTop = window.scrollY;
  document.querySelectorAll('.jj-hero, .jj-eras__grid, .jj-leagues__grid, .jj-jackpot__body').forEach((element) => {
    const node = /** @type {HTMLElement} */ (element);
    const previous = node.style.display;
    node.style.display = 'none';
    void node.offsetHeight;
    node.style.display = previous;
  });
  document.querySelectorAll('.jj-rail, .jj-posts, .jj-fits__grid, .jj-eras__grid, .jj-jackpot__list').forEach((rail) => {
    rail.scrollLeft = 0;
  });
  window.scrollTo(0, scrollTop);
}
let relayoutTimer = 0;
const queueRelayout = () => {
  clearTimeout(relayoutTimer);
  relayoutTimer = window.setTimeout(relayoutAfterRotation, 180);
};
window.addEventListener('orientationchange', queueRelayout);
window.addEventListener('resize', queueRelayout);

/**
 * Where a fresh visit opens. In-app browsers (Instagram, Facebook) can open a page partway down,
 * so a link with no #anchor is held at the top until the shopper starts scrolling themselves.
 * A spin link (jerseyjackpot.ca/#spin, or #jackpot) opens on the slot machine instead, just
 * under the sticky header, and is re-aligned as the pictures above it finish loading.
 * Back/forward and reloads are left alone, so the browser can restore where the shopper was.
 */
(() => {
  let navigation = null;
  try {
    navigation = performance.getEntriesByType('navigation')[0] || null;
  } catch {
    navigation = null;
  }
  const type = navigation ? /** @type {any} */ (navigation).type : 'navigate';
  if (type === 'back_forward' || type === 'reload') return;
  const hash = location.hash.toLowerCase();
  const spinLink = hash === '#spin' || hash === '#jackpot';
  if (location.hash && !spinLink) return;

  let touched = false;
  const mark = () => (touched = true);
  ['touchstart', 'wheel', 'keydown', 'pointerdown'].forEach((name) =>
    window.addEventListener(name, mark, { once: true, passive: true })
  );

  const headerOffset = () => {
    const header = document.querySelector('.shopify-section[id$="__header_section"]');
    if (!header || !['sticky', 'fixed'].includes(getComputedStyle(header).position)) return 0;
    return header.getBoundingClientRect().height;
  };
  const target = () => {
    if (!spinLink) return 0;
    const machine = document.querySelector('[data-jj-jackpot]');
    const section = machine ? machine.closest('.shopify-section') || machine : null;
    if (!section) return 0;
    return Math.max(0, Math.round(section.getBoundingClientRect().top + window.scrollY - headerOffset()));
  };
  const place = () => {
    if (touched) return;
    const top = target();
    if (Math.abs(window.scrollY - top) > 2) window.scrollTo({ top, left: 0, behavior: 'instant' });
  };
  const settle = () => {
    place();
    [300, 800, 1500, 2500].forEach((delay) => window.setTimeout(place, delay));
  };

  if (spinLink && document.readyState !== 'loading') place();
  else if (spinLink) document.addEventListener('DOMContentLoaded', place, { once: true });
  if (document.readyState === 'complete') settle();
  else window.addEventListener('load', settle, { once: true });
})();

function init(root = document) {
  initHero(root);
  initAutoScroll(root);
  initCountdowns(root);
  initJackpot(root);
  initCutOuts(root);
  initSearch();
  syncAddButtons();
  initFilters(root);
  initFacets(root);
  initClubIndex(root);
  initRecentlyViewed(root);
  initDialogs(root);
  initStockOrder();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}
initImageWarmup();
window.addEventListener('pageshow', (event) => {
  if (event.persisted) refreshCart();
});
document.addEventListener('shopify:section:load', (event) => init(/** @type {HTMLElement} */ (event.target)));
